import React, { useEffect, useState } from "react";
import PIScreenTable from "../PIScreenTable/PIScreenTable";
import PIScreenForm from "../PIScreenForm/PIScreenForm";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import { getUserInfo } from "../../../services/authService-sighup";
import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getMetricHeaders } from "../../ApiHeaders/apiHeadersTimeseries";

import apiClientAdmin from "../../../services/apiClientAdmin";
import { getHeaders as getAssetHeaders } from "../../ApiHeaders/apiHeaders";
import LoadingStyle from "../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";

interface PIScreenSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  prefillResourceName?: string | null;
  clearPrefill?: () => void;
  /** Admin on this page: may create, edit and delete. Otherwise the page is view-only. */
  canDelete?: boolean;
}

type PiMetricRecord = {
  PI_METRIC_ID: number;
  [key: string]: any;
};


const PIScreenSetup: React.FC<PIScreenSetupProps> = ({
  registerTabSwitchGuard,
  prefillResourceName,
  clearPrefill,
  canDelete = false
}) => {
  // const [piRecords, setPiRecords] = useState<any[]>([]);


  const [searchPrefill, setSearchPrefill] = useState<string | null>(null);
  useEffect(() => {
    if (prefillResourceName) {
      setSearchPrefill(prefillResourceName);
    }
  }, [prefillResourceName]);
  

  const [selectedPIRecord, setSelectedPIRecord] = useState<any | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );

  const [user, setUser] = useState<{ fullName: string; email: string } | null>(
    null
  );

  const [formResetKey, setFormResetKey] = useState(0);


  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);

  type FormMode = "create" | "edit" | "view" | null;

  const normalize = (v?: string) => v?.trim().toUpperCase() ?? "";

  const [assets, setAssets] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);

  const [piRecords, setPiRecords] = useState<PiMetricRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);



  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  useEffect(() => {
    const info = getUserInfo();
    if (info) {
      setUser({
        fullName: info.fullName || "Unknown User",
        email: info.email || "",
      });
    }
  }, []);

  const modifiedBy = user ? user.email || user.fullName : "Unknown User";

  useEffect(() => {
    if (!registerTabSwitchGuard) return;

    registerTabSwitchGuard(() => {
      return new Promise<boolean>((resolve) => {
        // if (isFormDirty || isCreateMode) {
        if (isFormDirty) {
          setDialog({
            open: true,
            title: "Unsaved Changes",
            message:
              "You have unsaved changes. Are you sure you want to discard them?",
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            onConfirm: () => {
              setIsFormDirty(false);
              setIsCreateMode(false);
              closeDialog();
              resolve(true);
            },
            onCancel: () => {
              closeDialog();
              resolve(false);
            },
          });
        } else {
          resolve(true);
        }
      });
    });
  }, [isFormDirty, isCreateMode]);

  useEffect(() => {
    const handler = () => {
      setSelectedPIRecord(null);
      setSelectedRow(null);
      setIsCreateMode(true);
    };
  
    window.addEventListener("pi-clear-selection", handler);
    return () => window.removeEventListener("pi-clear-selection", handler);
  }, []);
  

  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const backendMessage =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage;
        break;
      case 401:
      case 403:
        title = "Access Denied";
        details = "You are not authorized to perform this action.";
        break;
      case 404:
        title = "Not Found";
        details = backendMessage;
        break;
      case 500:
        title = "Server Error";
        details = backendMessage || "Please try again later.";
        break;
      default:
        title = fallbackTitle;
        details = backendMessage || "Unexpected error.";
    }

    setError({ title, details });
  };

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const headers = await getAssetHeaders();
  
        const assetRes = await apiClientAdmin.get("/assets/?page_size=1000", {
          headers,
        });
        const assetsData = assetRes.data.response?.data || [];
  
        const resourceRes = await apiClientAdmin.get("/ref-data/resources", {
          headers,
        });
        const resources = resourceRes.data.response?.data || [];
  
        const assetsWithResources = assetsData
        .filter((a: any) =>
          ["Active", "Onboarding"].includes(a.group_status_code)
        )
        .map((a: any) => ({
          ...a,
          resources: resources.filter(
            (r: any) => r.asset_id === a.asset_id
          ),
        }));


  
        setAssets(assetsWithResources);
      } catch (err) {
        handleApiError(err, "Failed to fetch assets/resources");
      }
    };
  
    fetchAssets();
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const headers = await getMetricHeaders();
        const res = await apiClientTimeseries.get("/metric-config/", {
          headers,
        });
  
        const piMetrics = (res.data.response?.data || []).filter(
          (m: any) => normalize(m.source) === "PI"
        );
  
        setMetrics(piMetrics);
      } catch (err) {
        handleApiError(err, "Failed to fetch metrics");
      }
    };
  
    fetchMetrics();
  }, []);
  

  const fetchPIRecords = async () => {
    setLoading(true);
    try {
      const headers = await getMetricHeaders();

      const response = await apiClientTimeseries.get(
        `/metric-resource/pi-metric-resource`,
        { headers }
      );

      const data = response?.data?.response?.data || [];

      const validPIRecords = data.filter(
        (r: PiMetricRecord) => r.PI_METRIC_ID !== null && r.PI_METRIC_ID !== undefined
      );
      

        setPiRecords(validPIRecords);

    } catch (err) {
      handleApiError(err, "Failed to Fetch PI Records");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPIRecords();
  }, []);

  const handleCreatePIRecord = async (payload: any) => {
    try {
      const headers = await getMetricHeaders();
  
      await apiClientTimeseries.post(
        `/metric-resource/pi-metric-resource`,
        payload,
        { headers }
      );
  
      await fetchPIRecords();
  
      //  FULL HARD RESET OF FORM
      setSelectedPIRecord(null);
      setSelectedRow(null);
      setIsFormDirty(false);
      setIsCreateMode(true);
      setFormResetKey((k) => k + 1);   
  
      setDialog({
        open: true,
        title: "Success",
        message: "PI Metric Resource created successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Create PI Record");
    }
  };
  
  

  const handleUpdatePIRecord = async (piMetricId: number, payload: any) => {
    try {
      const headers = await getMetricHeaders();
  
      await apiClientTimeseries.put(
        `/metric-resource/pi-metric-resource`,
        payload,
        { headers }
      );
  
      //  Reload table data
      const res = await apiClientTimeseries.get(
        `/metric-resource/pi-metric-resource`,
        { headers }
      );
  
      const data = res?.data?.response?.data || [];
      const validPIRecords = data.filter(
        (r: PiMetricRecord) => r.PI_METRIC_ID !== null && r.PI_METRIC_ID !== undefined
      );
      
  
      setPiRecords(validPIRecords);
  
      //  Re-select the updated row
      const updated = validPIRecords.find(
        (r: PiMetricRecord) => r.PI_METRIC_ID === piMetricId
      );
        if (updated) {
        setSelectedPIRecord(updated);
        setSelectedRow(updated.PI_METRIC_ID);
      }
  
      //  Force parent into VIEW mode
      setIsFormDirty(false);
      setIsCreateMode(false);   
  
      setDialog({
        open: true,
        title: "Success",
        message: "PI Metric Resource updated successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Update PI Record");
    }
  };
  
  

  const handleDeletePIRecord = async (piMetricId: number) => {
    try {
      const headers = await getMetricHeaders();
  
      await apiClientTimeseries.delete(
        `/metric-resource/pi-metric-resource/${piMetricId}`,
        { headers }
      );
  
      await fetchPIRecords();
  
      //  Reset screen to Create mode
      setSelectedPIRecord(null);
      setSelectedRow(null);
      setIsFormDirty(false);
      setIsCreateMode(true);
  
      setDialog({
        open: true,
        title: "Deleted",
        message: "PI Metric Resource deleted successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Delete PI Record");
    }
  };
  

  const handleRowSelect = (record: any) => {
    // if (isFormDirty || isCreateMode) {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedPIRecord(record);
          setSelectedRow(record.PI_METRIC_ID);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedPIRecord(record);
      setSelectedRow(record.PI_METRIC_ID);
    }
  };

  const handleClearSelection = () => {
    setSelectedPIRecord(null);
    setSelectedRow(null);
  };


  

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row bg-gray-100 p-4 gap-4 min-h-full ${split.containerClassName}`}>
      {/* TABLE */}
      <div className={`admin-tab-panel flex-grow bg-white relative ${split.leftClassName}`}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
          <LoadingStyle />
        </div>
      )}
      <PIScreenTable
        records={piRecords}
        selectedRow={selectedRow}
        onRowSelect={handleRowSelect}
        onClearSelection={handleClearSelection}
        searchPrefill={searchPrefill}
        onSearchUsed={() => {
          setSearchPrefill(null);
          clearPrefill?.();
        }}
      />
      </div>


      {/* FORM */}
      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow bg-white rounded-xl shadow-md">
      <PIScreenForm
        key={formResetKey} 
        records={piRecords}
        selectedRecord={selectedPIRecord}
        assets={assets}
        metrics={metrics}
        user={user}
        isCreateMode={isCreateMode}  
        onCreate={handleCreatePIRecord}
        onUpdate={handleUpdatePIRecord}
        onDelete={handleDeletePIRecord}
        onDirtyChange={setIsFormDirty}
        onModeChange={(mode: FormMode) => setIsCreateMode(mode === "create")}
        canDelete={canDelete}
        />
      </div>

      {/* DIALOGS */}
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}

      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default PIScreenSetup;
